import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { FileText, Plus } from 'lucide-react';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { weeklyReportApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type { WeeklyReportSummary } from '../types/weekly-report.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 対象週（YYYY-MM-DD）を「YYYY/MM/DD週」表記に整形する。
 * @graph-connects none
 */
function formatWeekOf(weekOf: string, t: TFunction): string {
  const [y, m, d] = weekOf.split('-');
  return y && m && d ? t('weeklyReport.weekOf', { date: `${y}/${m}/${d}` }) : weekOf;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 公開状態のバッジ。published=公開、draft=下書き。
 * @graph-connects none
 */
function StatusBadge({ status }: { status: WeeklyReportSummary['status'] }) {
  const { t } = useTranslation();
  const isPublished = status === 'published';
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        isPublished
          ? 'bg-[var(--color-success)]/15 text-[var(--color-success)]'
          : 'bg-[var(--color-bg-hover)] text-[var(--color-text-muted)]'
      }`}
    >
      {isPublished ? t('weeklyReport.published') : t('weeklyReport.draft')}
    </span>
  );
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business エアクロメンズ週次レポートの一覧ページ。公開済みレポートを対象週の新しい順に表示する。編集者には下書きと「新規作成」ボタンも表示する。
 * @graph-connects weeklyReportApi [calls] 週次レポート一覧 API
 * @graph-connects queryKeys [calls] react-query キーファクトリ
 */
export function WeeklyReportListPage() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.weeklyReports.list(),
    queryFn: weeklyReportApi.list,
  });

  const canEdit = data?.canEdit ?? false;
  const reports = data?.reports ?? [];

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-10">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <div className="mb-1 font-mono text-xs tracking-widest text-[var(--color-accent-primary)] uppercase">
            Weekly Report
          </div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('weeklyReport.title')}
          </h1>
          <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
            {t('weeklyReport.description')}
          </p>
        </div>
        {canEdit ? (
          <Link to="/weekly-report/new">
            <Button variant="primary" leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}>
              {t('common.createNew')}
            </Button>
          </Link>
        ) : null}
      </div>

      {isLoading ? (
        <p className="text-sm text-[var(--color-text-muted)]">{t('app.loading')}</p>
      ) : error ? (
        <Card className="p-6 text-sm text-[var(--color-error)]">
          {t('weeklyReport.fetchError')}
        </Card>
      ) : reports.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-10 text-center">
          <FileText className="h-8 w-8 text-[var(--color-text-muted)]" aria-hidden="true" />
          <p className="text-sm text-[var(--color-text-secondary)]">{t('weeklyReport.noData')}</p>
        </Card>
      ) : (
        <ul className="flex flex-col gap-3">
          {reports.map((report) => (
            <li key={report.id}>
              <Link
                to="/weekly-report/$id"
                params={{ id: report.id }}
                className="block rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-5 py-4 transition-colors hover:border-[var(--color-accent-primary)]"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono text-xs text-[var(--color-text-muted)]">
                    {formatWeekOf(report.weekOf, t)}
                  </span>
                  {canEdit ? <StatusBadge status={report.status} /> : null}
                </div>
                <h2 className="mt-1 truncate text-base font-semibold text-[var(--color-text-primary)]">
                  {report.title}
                </h2>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
